import { AllCommunityModule, ColDef, ModuleRegistry, themeAlpine } from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { rgba } from 'polished';
import { ReactNode, useMemo } from 'react';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { tokens } from '../theme/tokens';
import { DataGridHeader } from './DataGridHeader';
import { Empty } from './Empty';
import { Skeleton } from './Skeleton';

ModuleRegistry.registerModules([AllCommunityModule]);

// `display: contents` keeps the grid layout untouched while still letting the
// (inherited) font-smoothing properties cascade into the grid cells.
const FontSmoothingWrapper = styled.div`
	.ag-theme-alpine,
	.ag-theme-balham,
	.ag-theme-quartz {
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}

	/* Or apply it globally to all AG Grid cells to prevent a blurry appearance */
	.ag-cell {
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}

	/* With domLayout="autoHeight" the grid grows to fit all rows, which puts
	   AG Grid's horizontal scrollbar all the way at the bottom of the grid.
	   Move it up to sit above the column headers instead (via flex order on
	   .ag-root), and keep it stuck to the top of the viewport once it would
	   scroll out of view. The overflow overrides are needed because
	   position: sticky is inert inside an overflow: hidden ancestor; with
	   autoHeight nothing scrolls inside the grid itself, so relaxing them
	   is safe. The scrollbar is styled always-visible because overlay
	   scrollbars (macOS) would otherwise hide it between scrolls; AG Grid
	   sizes the bar itself (16px on overlay platforms). */
	.ag-layout-auto-height {
		&.ag-root,
		&.ag-root-wrapper {
			overflow: visible;
		}

		.ag-body-horizontal-scroll {
			order: -1;
			position: sticky;
			top: 0;
			/* Below StickyFilterHeader (z-index: 2) so filters stay clickable. */
			z-index: 1;
			background: ${(props) => props.theme.colors.white};
			/* AG Grid fades the scrollbar out between scrolls on overlay-scrollbar
			   platforms (ag-apple-scrollbar); keep it permanently visible. */
			opacity: 1;
			visibility: visible;
		}

		.ag-body-horizontal-scroll-viewport {
			/* Opts out of the app-wide thin scrollbar: in Chrome and Safari any
			   non-auto scrollbar-width makes the pseudo-element styling below
			   inert and collapses this bar into a 0px auto-hiding overlay. */
			scrollbar-width: auto;

			/* Only for browsers without ::-webkit-scrollbar (Firefox): in Chrome and
			   Safari setting scrollbar-color would disable the pseudo-element
			   styling below and re-enable the auto-hiding overlay scrollbar. */
			@supports not selector(::-webkit-scrollbar) {
				scrollbar-color: ${tokens.border.subtle} ${tokens.surface.primary};
			}

			&::-webkit-scrollbar {
				height: 16px;
				background: ${tokens.surface.primary};
			}

			&::-webkit-scrollbar-thumb {
				background: ${tokens.border.subtle};
				border: 4px solid ${tokens.surface.primary};
				border-radius: 8px;
			}
		}
	}
`;

type DataGridProps<TData> = AgGridReactProps<TData> & {
	emptyState?: {
		icon?: ReactNode;
		title?: string;
		description?: string;
	};
	themeParams?: DataGridThemeParams;
};

const baseDefaultColDef = {
	sortable: true,
	filter: false,
	resizable: false,
	suppressMovable: true,
	flex: 1,
	cellStyle: { display: 'flex', alignItems: 'center' },
	// Custom header that keeps native sorting and allows an extra component to be
	// rendered in the column header via `headerComponentParams.extra`.
	headerComponent: DataGridHeader,
} satisfies ColDef;

export type DataGridThemeParams = Parameters<typeof themeAlpine.withParams>[0];

/**
 * Shared AG Grid wrapper that applies the Treams look & feel (theme, spacing and
 * sensible defaults) so individual grids don't have to repeat any styling.
 *
 * It accepts all `AgGridReactProps`, and every styling-related default below can
 * be overridden per usage when really needed.
 * @param root0 The AG Grid props forwarded to the underlying grid.
 * @param root0.defaultColDef Column defaults merged on top of the shared Treams defaults.
 * @param root0.themeParams AG Grid theme params merged on top of the shared Treams theme.
 */
export const DataGrid = Skeleton(function DataGrid<TData>({
	defaultColDef,
	emptyState,
	themeParams,
	...props
}: DataGridProps<TData>) {
	const theme = useTheme();

	const gridTheme = useMemo(
		() =>
			themeAlpine.withParams({
				accentColor: theme.colors.primary,
				rowHoverColor: tokens.surface.primary,
				selectedRowBackgroundColor: rgba(theme.colors.primary, 0.05),
				headerBackgroundColor: theme.colors.white,
				headerFontSize: 15,
				dataFontSize: 15,
				borderColor: tokens.border.subtle,
				pinnedColumnBorder: false,
				wrapperBorder: false,
				fontFamily: 'inherit',
				textColor: tokens.text.secondary,
				headerTextColor: tokens.text.primary,
				...themeParams,
			}),
		[theme, themeParams],
	);

	if (emptyState && (props.rowData == null || props.rowData.length === 0)) {
		return (
			<Flex py={5} justifyContent="center">
				<Empty
					small
					icon={emptyState.icon}
					title={emptyState.title}
					description={emptyState.description}
				/>
			</Flex>
		);
	}

	return (
		<FontSmoothingWrapper>
			<AgGridReact<TData>
				theme={gridTheme}
				headerHeight={44}
				rowHeight={48}
				domLayout="autoHeight"
				suppressCellFocus
				animateRows={false}
				defaultColDef={{ ...baseDefaultColDef, ...defaultColDef }}
				{...props}
			/>
		</FontSmoothingWrapper>
	);
}, DataGridSkeleton);

// Horizontal padding roughly matches AG Grid's alpine cell padding so the
// skeleton lines up with the real grid once data has loaded.
const SkeletonCell = styled(Box)<{ $flex: number }>`
	flex: ${(props) => props.$flex};
	min-width: 0;
	padding: 0 17px;
`;

const SkeletonHeaderRow = styled(Flex)`
	height: 44px;
	align-items: center;
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlueLight};
`;

const SkeletonRow = styled(Flex)`
	height: 48px;
	align-items: center;
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlueLight};
`;

function DataGridSkeleton({ columns = 4, rows = 5 }: { columns?: number; rows?: number }) {
	const columnFlex = (index: number) => (index === 0 ? 2 : 1);

	return (
		<Box>
			<SkeletonHeaderRow>
				{[...new Array(columns)].map((_, index) => (
					<SkeletonCell key={index} $flex={columnFlex(index)}>
						<Skeleton.Box height="14px" width={index === 0 ? '60%' : '45%'} />
					</SkeletonCell>
				))}
			</SkeletonHeaderRow>
			<Skeleton.Repeat count={rows}>
				<SkeletonRow>
					{[...new Array(columns)].map((_, index) => (
						<SkeletonCell key={index} $flex={columnFlex(index)}>
							<Skeleton.Box height="14px" width={index === 0 ? '80%' : '55%'} />
						</SkeletonCell>
					))}
				</SkeletonRow>
			</Skeleton.Repeat>
		</Box>
	);
}
